import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { SortOrder } from 'antd/es/table/interface';
import { RequestData } from '@ant-design/pro-table';
import { Button, Tag } from 'antd';
import {
  EuroCircleOutlined, ExclamationCircleFilled, FileTextOutlined, PlusCircleOutlined,
} from '@ant-design/icons';
import { ProColumns } from '@ant-design/pro-table/es/typing';
import { AnyObject } from '@triare/auth-redux';
import { RequestOptionsType } from '@ant-design/pro-utils/es/typing';
import { createDefaultHiddenColumns, getSorterParams, queryFilterParams } from '../../../utils';
import Table, { listAction } from '../../Common/Table';
import {
  OrderTable,
  useDeliveryServiceGetAll,
  useOrderCopy,
  useOrderDelete,
  useOrderModeOfTransportsGet,
  useOrdersTableGet,
  useOrderStatusesGet,
} from '../../../hooks/api/order';
import { useMessageError, useMessageSuccess } from '../../../hooks/common';
import { dateFormat } from '../../../contstant';
import { useSimpleModal } from '../../Common/Modal/Simple';
import { useAuth } from '../../../store/auth';
import { DeliveryServiceList } from './Form/Common/SelectDeliveryService';
import { InvoiceStatusDisplay, OrderStatus } from './Adapter/enums';
import { getInvoiceStatusIcon, getPrioritizedInvoice, getVisibleInvoices } from './View/InvoicesList';
import IconWithBadge from '../../Common/IconWithBadge';
import { getOfferStatusIcon, getPrioritizedOffers, getVisibleOffers } from './View/OffersList';
import { isRoleEnough } from '../../../enums/user';
import { useAdministratorsGet } from '../../../hooks/api/users';
import { CompanyDisplayName } from '../../../enums/companies';

export enum OrderStatusColor {
  'draft' = 'purple',
  'pending' = 'gold',
  'shipped out' = 'cyan',
  'to be paid' = 'geekblue',
  'expired' = 'magenta',
  'ready for delivery' = 'geekblue',

  'declined' = 'default',

  'in progress' = 'orange',

  'processing' = 'blue',

  'rejected' = 'red',

  'on the way' = 'cyan',

  'delivered' = 'green',

  'active' = 'green',
}

export function getOrderStatusColor(status: string): string {
  // @ts-ignore take a color from the list
  return OrderStatusColor[status?.toLowerCase()] || 'rgba(0,0,0,.15)';
}

function ToolBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return [
    <Button
      key="add"
      icon={<PlusCircleOutlined />}
      type="primary"
      onClick={(e) => {
        e.preventDefault();
        if (pathname.includes('/companies/')) {
          navigate('../../orders/create');
        } else if (pathname === '/') {
          navigate('orders/create');
        } else {
          navigate('create');
        }
      }}
    >
      Add
    </Button>,
  ];
}

function CopyButton({ record, setTableKey }: { record: OrderTable } & AnyObject) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const orderCopy = useOrderCopy(user?.role === 'user');

  useMessageError([orderCopy]);
  useMessageSuccess([orderCopy], 'Duplicate created successfully');

  useEffect(() => {
    if (!orderCopy.error && !orderCopy.loading && orderCopy.data) {
      navigate(`/orders/${orderCopy.data.id}/edit`);
    }
  }, [orderCopy.data]);

  const ActionButton = listAction.copy;

  if (user?.id === record.id) {
    return null;
  }

  return (
    <ActionButton
      loading={orderCopy.loading}
      title="Copy order"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        orderCopy.fetch({
          id: record.id,
        });
      }}
    />
  );
}

function DeleteButton({ record, setTableKey }: { record: OrderTable } & AnyObject) {
  const { open, contextHolder } = useSimpleModal();
  const { user } = useAuth();
  const orderDelete = useOrderDelete(undefined, user?.role === 'user');

  useMessageError([orderDelete]);
  useMessageSuccess([orderDelete], 'Order deleted successfully');

  useEffect(() => {
    if (!orderDelete.error && !orderDelete.loading && orderDelete.data) {
      setTableKey(Date.now());
    }
  }, [orderDelete.error, orderDelete.loading, orderDelete.data]);

  const ActionButton = listAction.delete;

  if (user?.id === record.id) {
    return null;
  }

  return (
    <>
      <ActionButton
        loading={orderDelete.loading}
        title="Delete order"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          open({
            icon: <ExclamationCircleFilled />,
            title: 'Delete order?',
            content: (
              <span>
                Are you sure you want to delete order
                {' '}
                <b>{record.orderNumber}</b>
                ?
              </span>
            ),
            cancelText: 'Cancel',
            okText: 'Delete',
            okButtonProps: {
              danger: true,
            },
            onOk: () => orderDelete.fetch(record.id),
          });
        }}
        className={user?.role === 'user' && (
          record?.status && ![
            OrderStatus.DRAFT, OrderStatus.PENDING, OrderStatus.TO_BE_PAID, OrderStatus.REJECTED]
            .includes(record?.status))
          ? 'hidden-element' : undefined}
      />
      {contextHolder}
    </>
  );
}

function Orders({ companyId }: { companyId?: string }): React.ReactNode | null {
  const navigate = useNavigate();
  const { user } = useAuth();
  const ordersGet = useOrdersTableGet(user?.role === 'user');
  const [tableKey, setTableKey] = useState<number>(Date.now());
  const [, setSearchParams] = useSearchParams();
  const orderStatusesGet = useOrderStatusesGet(undefined, false);
  const deliveryServiceGetAll = useDeliveryServiceGetAll(undefined, false);
  const orderModeOfTransportsGet = useOrderModeOfTransportsGet(undefined, false);
  const usersManagersGet = useAdministratorsGet(
    (data) => (data?.data || [])
      .map((userItem) => ({
        value: userItem?.id,
        label: userItem?.fullName ? `${userItem.fullName} (${userItem?.email || '-'})` : userItem?.email || '',
      })),
  );

  const isUserAdmin = isRoleEnough(user?.role, 'admin');

  useMessageError([ordersGet, orderStatusesGet, deliveryServiceGetAll, orderModeOfTransportsGet]);

  const columns: ProColumns<OrderTable>[] = useMemo(() => ([
    {
      key: 'orderNumber',
      title: 'Order No.',
      dataIndex: 'orderNumber',
      sorter: true,
      hideInSearch: true,
      // render: (text, record) => {
      //   if (window.location.pathname.includes('/companies/')) {
      //     return (
      //       <NavLink to={`../orders/${record.id}`}>{text}</NavLink>
      //     );
      //   }

      //   return (
      //     <NavLink to={window.location.pathname === '/' ? `orders/${record.id}` : record.id}>{text}</NavLink>
      //   );
      // },
    },
    {
      key: 'createdAt',
      title: 'Date added',
      dataIndex: 'createdAt',
      valueType: 'date',
      sorter: true,
      hideInSearch: true,
      render: (text, record) => (
        record.createdAt?.format(dateFormat)
      ),
    },
    {
      key: 'createdAt',
      title: 'Date added',
      dataIndex: 'createdAt',
      valueType: 'dateRange',
      hideInTable: true,
      search: {
        transform: (value) => (value ? ({
          dateAddedFrom: value[0],
          dateAddedTo: value[1],
        }) : undefined),
      },
    },
    {
      key: 'modeOfTransport',
      title: 'Mode of Transport',
      dataIndex: 'modeOfTransport',
      valueType: 'select',
      request: () => new Promise((resolve) => {
        orderModeOfTransportsGet.fetch().then((data) => resolve((data || []).map((item) => item)))
          .catch(() => resolve([]));
      }),
    },
    {
      key: 'deliveryService',
      title: 'Delivery service',
      dataIndex: 'deliveryService',
      valueType: 'select',
      request: () => new Promise((resolve) => {
        deliveryServiceGetAll.fetch().then((data) => resolve((data || []).map(
          (item) => DeliveryServiceList.find(({ value }) => value === item?.value),
        ).filter((value) => !!value) as RequestOptionsType[])).catch(() => resolve([]));
      }),
    },
    {
      key: 'trackingNumber',
      title: 'Tracking No.',
      dataIndex: 'trackingNumber',
      hideInSearch: true,
    },
    {
      key: 'company',
      title: 'Consignee', // Importer Company
      dataIndex: 'importer.company',
      hideInSearch: true,
      hideInTable: user?.role !== 'user',
      render: (_, record) => record?.importer?.company,
    },
    {
      key: 'invoiceStatus',
      title: 'Invoice status',
      dataIndex: 'invoiceStatus',
      valueType: 'select',
      valueEnum: InvoiceStatusDisplay,
      hideInTable: true,
      hideInSetting: true,
    },
    {
      key: 'managerId',
      title: 'Manager',
      dataIndex: 'manager',
      hideInTable: !isUserAdmin,
      hideInSearch: !isUserAdmin,
      hideInSetting: !isUserAdmin,
      hideInForm: !isUserAdmin,
      render: (_, record) => (
        <i>
          {user?.id === record?.manager?.id
            ? <b>You are manager</b>
            : (record?.manager?.fullName || record?.manager?.email || '-')}
        </i>
      ),
      valueType: 'select',
      request: () => new Promise((resolve) => {
        usersManagersGet.fetch({ roles: 'admin,root'.split(','), pageSize: 100 })
          .then((data) => resolve((data || []).map((item) => item)))
          .catch(() => resolve([]));
      }),
    },
    {
      key: 'companyName',
      title: CompanyDisplayName.companyName, // Company
      dataIndex: 'companyName',
      hideInSearch: true,
      hideInTable: user?.role === 'user',
      render: (_, record) => record?.company?.companyName,
    },
    {
      key: 'displayName',
      title: CompanyDisplayName.displayNameFull,
      dataIndex: 'company.displayName',
      hideInSearch: true,
      hideInTable: user?.role === 'user',
      renderText: (text, record) => record?.company?.displayName,
      // renderText: (_, { company }) => (
      //   company?.companyName === company?.displayName ? <i>Same as customer</i> : company?.displayName
      // ),
    },
    {
      key: 'shipper',
      title: 'Shipper',
      dataIndex: 'shipper',
      hideInSearch: true,
      render: (_, record) => record.shipper?.company || '-',
    },
    {
      key: 'reference',
      title: 'Shipper Reference',
      dataIndex: 'reference',
      hideInSearch: true,
      render: (_, record) => record.shipper?.reference || '-',
    },
    {
      key: 'importer',
      title: 'Importer',
      dataIndex: 'importer',
      hideInSearch: true,
      render: (_, record) => record.importer?.company || '-',
    },
    {
      key: 'offers',
      title: 'Offers',
      dataIndex: 'offers',
      hideInSearch: true,
      render: (_, record) => {
        const foundOffers = getVisibleOffers(record.offers || [], user?.role);
        const prioritizedOffer = getPrioritizedOffers(foundOffers);
        const iconConfig = getOfferStatusIcon(prioritizedOffer?.offerStatus);

        return prioritizedOffer ? (
          <IconWithBadge BadgeIcon={iconConfig.icon} Icon={FileTextOutlined} badgeColor={iconConfig.color} />
        ) : '-';
      },
    },
    {
      key: 'invoices',
      title: 'Invoices',
      dataIndex: 'invoices',
      hideInSearch: true,
      render: (_, record) => {
        const foundInvoices = getVisibleInvoices(record.invoices || [], user?.role);
        const prioritizedInvoice = getPrioritizedInvoice(foundInvoices);
        const iconConfig = getInvoiceStatusIcon(prioritizedInvoice?.status);

        return prioritizedInvoice ? (
          <IconWithBadge BadgeIcon={iconConfig.icon} Icon={EuroCircleOutlined} badgeColor={iconConfig.color} />
        ) : '-';
      },
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      sorter: true,
      order: 1,
      render: (_, record) => (
        <Tag color={getOrderStatusColor(record.status)}>{record.status}</Tag>
      ),
      valueType: 'select',
      request: () => new Promise((resolve) => {
        orderStatusesGet.fetch()
          .then((data) => resolve(Array.isArray(data) ? data : []))
          .catch(() => resolve([]));
      }),
    },
  ]), [orderStatusesGet, deliveryServiceGetAll, orderModeOfTransportsGet]);

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<OrderTable>>> => {
    const newParams = queryFilterParams({
      page: current ? `${current}` : '1',
      pageSize: pageSize ? `${pageSize}` : '10',
      ...args,
      ...getSorterParams(sorter),
    });

    if (companyId) {
      Object.assign(newParams, {
        company: companyId,
      });
    }

    setSearchParams(queryFilterParams({ ...args, ...getSorterParams(sorter) }), { replace: true });

    return ordersGet.fetch({
      ...newParams,
      tab: undefined,
    }).then((response) => {
      if (response) {
        const { data, total } = response;

        return ({ data: data || [], success: true, total });
      }

      return ({ data: [], success: false, total: 0 });
    });
  };

  const defaultHiddenColumns = useMemo(() => createDefaultHiddenColumns(
    ['modeOfTransport', 'shipper', 'reference', 'importer'],
  ), []);

  return (
    <Table<OrderTable>
      key={tableKey}
      className="transparent"
      rowClassName="cursor-pointer"
      headerTitle="List of orders"
      toolBarRender={ToolBar}
      request={tableRequest}
      columns={columns}
      onRow={(record) => ({
        onClick: (event) => {
          event.preventDefault();
          navigate(`/orders/${record.id}`);
        },
      })}
      actions={[
        ['form', ({ text, record }) => ({
          title: 'Edit form',
          onClick: (e: MouseEvent) => {
            e.stopPropagation();

            if (window.location.pathname.includes('/companies/')) {
              navigate(`../orders/${record.id}/edit`);
            } else {
              navigate(window.location.pathname === '/' ? `orders/${record.id}/edit` : `${record.id}/edit`);
            }
          },
          className: (user?.role === 'user' && (
            record?.status && ![OrderStatus.DRAFT, OrderStatus.TO_BE_PAID, OrderStatus.READY_FOR_DELIVERY,
              OrderStatus.IN_PROGRESS]
              .includes(record?.status)))
            || (isRoleEnough(user?.role, 'admin') && (
              record?.status && [OrderStatus.TO_BE_PAID].includes(record?.status)))
            ? 'hidden-element' : undefined,
        })],
        [CopyButton, {
          setTableKey,
        }],
        [DeleteButton, {
          setTableKey,
        }],
      ]}
      search={{
        searchText: 'Filter',
        resetText: 'Clear',
      }}
      options={{
        search: {
          name: 'search',
        },
      }}
      columnsState={{
        persistenceKey: 'pro-table-orders',
        persistenceType: 'localStorage',
        defaultValue: defaultHiddenColumns,
      }}
      // pagination={{ defaultPageSize: 50 }}
    />
  );
}

export default Orders;
